import GiftInfoRow from '@activities/gift/_shared/components/GiftInfoRow';
import Column from '@components/Column';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import useProject from '@hooks/useProject';
import ArrowLineDown from '@icons/arrow/ArrowLineDown';
import { GiftOrderResultResponse } from '@models/gift';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import useOrderInfo from './hooks/useOrderInfo';
import {
    formatDateTime,
    formatPhoneNo,
    getOrderUsedAt,
    getRecipientGiftLabel,
} from '../../utils';

interface Props {
    order: GiftOrderResultResponse;
}

function OrderInfo({ order }: Props) {
    const { t } = useTranslation('gift');
    const isDaouGift = useProject()?.giftVendor === 'DAOU';
    const [isExpanded, setIsExpanded] = useState(false);
    const {
        uiStatus, statusLabel, recipientSummary, giftLabels,
    } = useOrderInfo(order);
    const isSingleRecipient = order.recipients.length === 1;

    return (
        <List>
            <Row spacing={2} fullWidth justify='space-between'>
                <Label as='dt' color={colors.textSecondary}>{t('giftSent.receiver')}</Label>
                <RecipientValue>
                    {isSingleRecipient ? (
                        <Summary as='span' color={colors.textSecondary} align='right'>{recipientSummary}</Summary>
                    ) : (
                        <ExpandButton
                            type='button'
                            aria-expanded={isExpanded}
                            onClick={() => setIsExpanded((v) => !v)}>
                            <Summary as='span' color={colors.textSecondary} align='right'>{recipientSummary}</Summary>
                            <Arrow $expanded={isExpanded} size={20} color={colors.gray6} />
                        </ExpandButton>
                    )}
                </RecipientValue>
            </Row>

            {isExpanded && order.recipients.length > 0 && (
                <Column as="ul" spacing={2} fullWidth justify='space-between'>
                    {order.recipients.map((recipient) => (
                        <Row as="li" spacing={2} fullWidth justify='space-between' key={`${recipient.phoneNo}-${recipient.name}`}>
                            <InfoRow spacing={1}>
                                <NameText as='span' size="body-long" fontWeight='regular' color={colors.textSecondary} ellipsis>{recipient.name}</NameText>
                                <PhoneText as='span' size="body-long" fontWeight='regular' color={colors.textSecondary}>{formatPhoneNo(recipient.phoneNo)}</PhoneText>
                            </InfoRow>
                            <RecipientStatus as='span' color={colors.textSecondary} align='right'>
                                {getRecipientGiftLabel(recipient.gifts[0], giftLabels)}
                            </RecipientStatus>
                        </Row>
                    ))}
                </Column>
            )}

            <GiftInfoRow label={t('giftSent.giftStatus')}
                color={uiStatus === 'CANCELLED' ? colors.red : colors.textSecondary}>
                {statusLabel}
            </GiftInfoRow>
            <GiftInfoRow label={t('giftSent.sentAt')}>
                {formatDateTime(order.sentAt)}
            </GiftInfoRow>
            <GiftInfoRow label={t('giftSent.paymentAmount')}>
                {order.amount.toLocaleString()}{t('item.priceUnit')}
            </GiftInfoRow>
            {uiStatus === 'PAID' && (
                <GiftInfoRow label={t('giftSent.cancelableAt')}>
                    {formatDateTime(order.cancelableAt)}
                </GiftInfoRow>
            )}
            {uiStatus === 'USED' && isSingleRecipient && (
                <GiftInfoRow label={t('giftSent.usedAt')}>
                    {formatDateTime(getOrderUsedAt(order), isDaouGift ? 'YYYY.MM.DD' : 'YYYY.MM.DD HH:mm')}
                </GiftInfoRow>
            )}
            {uiStatus === 'CANCELLED' && (
                <GiftInfoRow label={t('giftSent.canceledAt')}>
                    {formatDateTime(order.canceledAt)}
                </GiftInfoRow>
            )}
        </List>
    );
}

const List = styled.dl`
    display: flex;
    width: 100%;
    margin: 0;
    flex-direction: column;
    gap: var(--spacing-2);
    padding: var(--spacing-1) 0;
`;

const Label = styled(Text)`
    flex-shrink: 0;
`;

const RecipientValue = styled.dd`
    margin: 0;
    min-width: 0;
`;

const ExpandButton = styled.button`
    display: inline-flex;
    max-width: 100%;
    align-items: center;
    gap: var(--spacing-0-5);
    margin: 0;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
`;

const Summary = styled(Text)`
    min-width: 0;
    word-break: break-word;
`;

const Arrow = styled(ArrowLineDown)<{ $expanded: boolean }>`
    flex-shrink: 0;
    transform: rotate(${({ $expanded }) => ($expanded ? 180 : 0)}deg);
    transition: transform 0.2s ease;
`;

const InfoRow = styled(Row)`
    flex: 1;
    min-width: 0;
`;

const NameText = styled(Text)`
    min-width: 0;
`;

const PhoneText = styled(Text)`
    flex-shrink: 0;
`;

const RecipientStatus = styled(Text)`
    flex-shrink: 0;
`;

export default OrderInfo;
